/** Clip (/clips/:clipId): detail, preview player, renders with status/QA, "Choose templates", MD. */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { DownloadVideo } from "../../components/DownloadVideo";
import { ErrorBox } from "../../components/ErrorBox";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { fmtMs } from "../../lib/frames";
import { Loading, Steps, useLiveInvalidate, when } from "../common";
import { qaLabel, type ClipDetail } from "../types";
import { ClipPreview } from "./ClipPreview";

const STATUS_STATE: Record<string, string> = { queued: "queued", running: "running", done: "done", failed: "failed",
  cancelled: "skipped", stale: "stale" };

export function ClipView() {
  const { clipId } = useParams();
  const id = Number(clipId);
  const q = useQuery({
    queryKey: qk.entity("clip", id),
    enabled: Number.isInteger(id) && id > 0,
    queryFn: () => unwrap(api.GET("/api/clips/{clip_id}", { params: { path: { clip_id: id } } })),
  });
  useLiveInvalidate((e) => (e.entity === "clip" && e.entity_id === id) || e.entity === "render", [qk.entity("clip", id)]);
  if (q.isPending) return <><PageHeader title={`Clip #${id}`} /><Loading what="clip" /></>;
  if (q.isError) return <section><PageHeader title={`Clip #${id}`} md={{ page: "clip", params: { id } }} /><ErrorBox error={q.error} /></section>;
  return <ClipLoaded c={q.data} />;
}

function ClipLoaded({ c }: { c: ClipDetail }) {
  const song = c.kind === "song";
  const songWin = c.t_song_in_ms != null && c.t_song_out_ms != null ? `${fmtMs(c.t_song_in_ms)}–${fmtMs(c.t_song_out_ms)}` : "–";
  const rows: [string, string][] = [
    ["Kind", song ? "song clip (no music video: the song's cover over the studio audio)" : "music-video clip"],
    ["MV in–out", song ? "none (song clip)" : `${fmtMs(c.t_mv_in_ms)}–${fmtMs(c.t_mv_out_ms)}`],
    ["Song in–out", song && songWin !== "–" ? `${songWin} + 0.7 s fade` : songWin],
    ["Length", fmtMs(c.dur_ms)],
    ["Origin", `${c.origin}${c.score != null ? ` (score ${c.score.toFixed(2)})` : ""}${c.reason ? `: ${c.reason}` : ""}`],
    ["State", c.state],
    ["Template", c.template_state.replace("_", " ")],
    ["Bounds", c.locked ? "locked" : "not locked"],
    ["Created", `${when(c.created_at)} by ${c.created_by}`],
    ["Version", String(c.version)],
  ];
  return (
    <section className="ws8-page" data-testid="clip-page">
      <PageHeader title={`Clip #${c.id}${c.label ? `: ${c.label}` : ""}`} md={{ page: "clip", params: { id: c.id } }}>
        <Link className="button ws8-primary" to={`/choose?clip=${c.id}`} data-testid="choose-templates">Choose templates</Link>
        {c.video_id != null && (
          <Link className="button" to={`/watch/${c.video_id}?in=${c.t_mv_in_ms}&out=${c.t_mv_out_ms}&t=${c.t_mv_in_ms}`}
            data-testid="open-watch">Open in Watch</Link>
        )}
        {c.song_id != null && (
          <Link className="button" to={`/songs/${c.song_id}`} data-testid="open-song">
            {c.song_title ? `Song: ${c.song_title}` : "Song"}
          </Link>
        )}
        <DownloadVideo mediaId={c.master_media_id ?? c.preview_media_id} />
      </PageHeader>
      {song && (
        <p className="ws8-muted" data-testid="song-clip-note">
          <span className="ws8-badge">song clip</span> No music video: made from official lyric lines on the
          {" "}{c.song_id != null ? <Link to={`/songs/${c.song_id}`}>song page</Link> : "song page"}. Only the
          synthetic (mg) and spin templates take it.
        </p>
      )}
      <Steps steps={c.steps} />
      <div className="ws8-render">
        <ClipPreview clip={c} loop={false} autoplay={false} />
        <section className="ws8-panel" aria-labelledby="ws8-clip-detail-h">
          <h2 id="ws8-clip-detail-h" className="ws8-h2">Detail</h2>
          <table className="ws8-table" data-testid="clip-detail">
            <tbody>{rows.map(([k, v]) => <tr key={k}><th scope="row">{k}</th><td>{v}</td></tr>)}</tbody>
          </table>
        </section>
      </div>
      <section className="ws8-panel" aria-labelledby="ws8-clip-renders-h">
        <h2 id="ws8-clip-renders-h" className="ws8-h2">Renders ({c.renders.length})</h2>
        {c.renders.length === 0 ? <p className="ws8-muted" data-testid="no-renders">No renders yet. Use “Choose templates”.</p> : (
          <table className="ws8-table" data-testid="clip-renders">
            <thead><tr><th scope="col">Render</th><th scope="col">Template</th><th scope="col">Status</th><th scope="col">QA</th>
              <th scope="col">Approved</th><th scope="col">Queued</th></tr></thead>
            <tbody>
              {c.renders.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/renders/${r.id}`}>#{r.id}</Link></td>
                  <td>{r.template_id}</td>
                  <td><StatusPill state={STATUS_STATE[r.status] ?? r.status} label={r.status} title={r.error ?? undefined} /></td>
                  <td>{qaLabel(r.qa_category)}{r.qa_actor ? ` · ${r.qa_actor}` : ""}</td>
                  <td>{r.approved ? "yes" : "no"}</td>
                  <td>{when(r.queued_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}
